import React from 'react';
import {
  AfterSubmitFunc,
  ButtonColor,
  FormBlock,
  IconType,
  InputLayoutIcon,
  isReactNative,
  RelativeRow,
  SubmitButton,
  TextInput
} from 'ui-builder';
import {
  CreateNewContactRequest,
  Name,
  PersonProfile,
  useProjectAssignmentsService
} from 'nekst-api';
import { PhoneNumbersInput, ProjectRolesMultiDropdown } from 'features/nekst-widgets';
import SearchContactTextInput from './SearchContactTextInput';
import { BasePersonFormTemplate } from '../../../People';

interface AddPersonToTransactionFormProps {
  className?: string;
  projectId: number;
  afterSubmitFunc?: AfterSubmitFunc<void>;
}

type AddTransactionPartyRequest = {
  personId: number,
  rolesIds: number[],
  profile: PersonProfile,
}

export function AddPersonToTransactionForm(props: AddPersonToTransactionFormProps) {
  const projectAssignmentsService = useProjectAssignmentsService();

  return (
    <BasePersonFormTemplate<CreateNewContactRequest | AddTransactionPartyRequest, void>
      submitFormFunc={async (data) => {
        if ('personId' in data && data.personId) {
          await projectAssignmentsService.addTransactionParty(
            props.projectId,
            {
              personId: data.personId,
              rolesIds: data.rolesIds,
            },
          );
        } else {
          await projectAssignmentsService.createNewContact(
            props.projectId,
            data as CreateNewContactRequest,
          );
        }
      }}
      afterSubmitFunc={props.afterSubmitFunc}
      className={props.className}
      validationSchema={projectAssignmentsService.getCreateNewContactValidationSchema()}
      buttons={(
        <SubmitButton
          text="Add"
          key="add-transaction-party-button"
          color={ButtonColor.BLUE}
          iconType={IconType.PLUS}
        />
      )}
      initialData={{
        profile: {
          name: {
            firstName: '',
            lastName: '',
          } as Name,
          primaryEmail: '',
          phoneNumbers: [],
        },
        rolesIds: [],
      }}
      title={!isReactNative() ? 'Add Person to Transaction' : undefined}
      clearAfterSubmit
    >
      <FormBlock>
        {isReactNative() ? (
          <>
            <TextInput
              source="profile.name.firstName"
              label="First Name"
              icon={InputLayoutIcon.PERSON}
            />
            <TextInput
              source="profile.name.lastName"
              label="Last Name"
              icon={InputLayoutIcon.PERSON}
            />
          </>
        ) : (
          <RelativeRow weights={[6, 6]}>
            <SearchContactTextInput source="profile.name.firstName" label="First Name" />
            <SearchContactTextInput source="profile.name.lastName" label="Last Name" />
          </RelativeRow>
        )}
        <RelativeRow weights={isReactNative() ? [12] : [6, 6]}>
          <TextInput source="profile.primaryEmail" label="Primary Email"
                     icon={InputLayoutIcon.EMAIL} />
          <TextInput source="profile.alternateEmail" label="Alternate Email"
                     icon={InputLayoutIcon.EMAIL} />
        </RelativeRow>
        <PhoneNumbersInput
          source="profile.phoneNumbers"
          label="Phone Numbers"
          openCreateForm
        />
        <RelativeRow weights={[6, 6]}>
          <ProjectRolesMultiDropdown
            source="rolesIds"
            label="Role(s)"
            icon={InputLayoutIcon.ASSIGNMENT}
            hideTeamMembersOnlyRoles
          />
        </RelativeRow>
      </FormBlock>
    </BasePersonFormTemplate>
  );
}
